import { Reveal } from "@/components/site/Reveal";
import { Link } from "@tanstack/react-router";
import { CALENDLY_URL } from "@/lib/site";
import { trackScheduleConsultation } from "@/lib/analytics";
import deepakPhoto from "@/assets/deepak-kumar.webp";
import { Linkedin, Mail, Calendar, Globe, ArrowRight, CheckCircle2 } from "lucide-react";

export function FounderSection() {
  return (
    <section className="relative overflow-hidden bg-[#060B14] py-24 px-6 text-white border-y border-white/5">
      {/* Background dot grid pattern */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.12]"
        style={{
          backgroundImage: "radial-gradient(#94a3b8 1px, transparent 1px)",
          backgroundSize: "28px 28px",
        }}
      />

      {/* Decorative radar reticle rings on the right (matches reference style) */}
      <div className="pointer-events-none absolute -right-24 top-1/2 -translate-y-1/2 hidden lg:block opacity-20">
        <div className="relative size-[640px]">
          <div className="absolute inset-0 rounded-full border border-brand-accent/40" />
          <div className="absolute inset-20 rounded-full border border-brand-accent/30" />
          <div className="absolute inset-40 rounded-full border border-brand-accent/25" />
          <div className="absolute inset-60 rounded-full border border-brand-accent/20" />
          <div className="absolute left-1/2 top-0 bottom-0 w-px bg-brand-accent/25 -translate-x-1/2" />
          <div className="absolute top-1/2 left-0 right-0 h-px bg-brand-accent/25 -translate-y-1/2" />
        </div>
      </div>

      <div className="relative mx-auto max-w-5xl">
        {/* Section Header */}
        <Reveal className="mb-12">
          <div className="flex items-center gap-2 text-xs font-mono font-bold tracking-widest text-brand-accent uppercase">
            WHO'S BEHIND IT
          </div>
          <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight sm:text-4xl text-white">
            Built by a working accountant, not a sales desk.
          </h2>
          <p className="mt-3 text-white/60 text-base max-w-2xl leading-relaxed">
            Not a faceless agency — Finendra is founded and operated by a hands-on finance
            practitioner who personally configures your charts of accounts, reconciles bank feeds,
            and closes the books directly.
          </p>
        </Reveal>

        {/* Founder Card */}
        <Reveal delay={0.1}>
          <div className="group relative rounded-3xl border border-white/10 bg-[#0B1220]/95 backdrop-blur-xl p-8 sm:p-10 shadow-2xl transition-all duration-300 hover:border-brand-accent/30 hover:shadow-brand-accent/5">
            {/* Top highlight line */}
            <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />

            <div className="flex flex-col md:flex-row gap-8 lg:gap-10 items-start">
              {/* Left Column: Avatar & Action Buttons */}
              <div className="shrink-0 flex flex-col items-center sm:items-start gap-5">
                <div className="relative">
                  <img
                    src={deepakPhoto}
                    alt="Deepak Kumar - Founder & Controller"
                    className="size-32 sm:size-36 rounded-full object-cover ring-2 ring-white/15 shadow-xl bg-brand-navy"
                    width={144}
                    height={144}
                    loading="lazy"
                  />
                  <div
                    className="absolute bottom-1 right-1 flex size-7 items-center justify-center rounded-full bg-emerald-500 text-white ring-4 ring-[#0B1220] shadow-md"
                    title="Verified Controller & ICAI Alum"
                  >
                    <CheckCircle2 className="size-4 stroke-[2.5]" />
                  </div>
                </div>

                {/* 4 Icon Action Buttons (matching screenshot) */}
                <div className="flex items-center gap-2.5">
                  <a
                    href="https://www.linkedin.com/in/deepak-kumar-8b1a2213b"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="LinkedIn Profile"
                    className="flex size-10 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-white/70 hover:text-white hover:border-brand-accent hover:bg-brand-accent/15 transition-all"
                  >
                    <Linkedin className="size-4" />
                  </a>
                  <a
                    href="mailto:connect@finendra.com"
                    title="Email Finendra"
                    className="flex size-10 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-white/70 hover:text-white hover:border-brand-accent hover:bg-brand-accent/15 transition-all"
                  >
                    <Mail className="size-4" />
                  </a>
                  <a
                    href={CALENDLY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackScheduleConsultation("founder_card")}
                    data-analytics-location="founder_card"
                    title="Book a 20-min Diagnostic Call"
                    className="flex size-10 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-white/70 hover:text-white hover:border-brand-accent hover:bg-brand-accent/15 transition-all"
                  >
                    <Calendar className="size-4" />
                  </a>
                  <Link
                    to="/about"
                    title="Read Full Founder Story & Background"
                    className="flex size-10 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-white/70 hover:text-white hover:border-brand-accent hover:bg-brand-accent/15 transition-all"
                  >
                    <Globe className="size-4" />
                  </Link>
                </div>
              </div>

              {/* Right Column: Bio, Credentials & Tag Badges */}
              <div className="flex-1 min-w-0 space-y-4">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-display text-2xl font-bold text-white tracking-tight">
                      Deepak Kumar
                    </h3>
                    <span className="font-mono text-xs text-white/45">@deepakkumar</span>
                  </div>
                  <p className="mt-1 text-xs font-mono font-bold uppercase tracking-wider text-brand-accent/90">
                    FOUNDER &amp; MANAGING CONTROLLER • B.COM, LL.B.
                  </p>
                </div>

                <p className="text-white/80 text-[14.5px] leading-relaxed">
                  Deepak Kumar is a full-charge accountant and financial controller with 9+ years of
                  hands-on international accounting experience spanning US, UK, and Indian
                  regulatory frameworks. Certified as an Intuit QuickBooks Online ProAdvisor and
                  holding dual degrees in Commerce (B.Com) and Law (LL.B.), he has directed
                  statutory and internal audits for premier commercial organizations including
                  Cipla, Lifestyle International, and Max Fashion.
                </p>

                <p className="text-white/70 text-[14px] leading-relaxed">
                  Finendra grew directly out of his work managing finance operations for high-growth
                  tech startups and cross-border businesses at Escalon and Avolon — solving the
                  exact pain point founders face: messy charts of accounts, broken Shopify/Stripe
                  revenue recognition, missed nexus thresholds, and chaotic due diligence. Instead
                  of handing off your financial ledger to junior offshore temps, every Finendra
                  engagement runs with dedicated controller oversight, daily bank feed hygiene, and
                  institutional month-end closes.
                </p>

                {/* Badges / Pill Tags (matching screenshot) */}
                <div className="flex flex-wrap gap-2 pt-2">
                  {[
                    "9+ Years International Experience",
                    "Certified QuickBooks ProAdvisor",
                    "Ex-Auditor (Cipla, Max, Lifestyle)",
                    "B.Com + LL.B. (Law)",
                    "Controller For 100+ Closes",
                  ].map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full border border-white/12 bg-white/5 px-3 py-1 text-[11px] font-mono uppercase tracking-wider text-white/75 hover:border-brand-accent/40 hover:text-white transition-colors"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </Reveal>

        {/* Footer Link below card */}
        <Reveal delay={0.2} className="mt-6 flex items-center justify-between px-2">
          <Link
            to="/about"
            className="group inline-flex items-center gap-2 text-sm font-semibold text-white/70 hover:text-white transition-colors"
          >
            <span>More about the founder &amp; full story</span>
            <ArrowRight className="size-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}
